import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './index';
import { hasuraClient } from '../utils/graphqlClient';
import { GET_ACTIVE_REPORTS_BY_DATE } from '../graphql/queries/reportManagement';
import { GET_REPORT_TYPES_QUERY } from '../graphql/queries/reportTypes';
import { formatDateForHasura } from '../utils/dateUtils';

const TODAY = formatDateForHasura(new Date());
const SEVEN_DAYS_FROM_NOW = formatDateForHasura(
  new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
);

const buildUserName = (user) => {
  if (!user) return null;
  const first = user.first_name || '';
  const last = user.last_name || '';
  const full = `${first} ${last}`.trim();
  return full || null;
};

const flattenJobReports = (
  jobs,
  reportTypeMap = {},
  inFlowKeySet = new Set(),
  financialRequestKeySet = new Set()
) => {
  const today = TODAY;
  const sevenDaysFromNow = SEVEN_DAYS_FROM_NOW;
  const allReports = [];

  for (const job of jobs) {
    const rawReports = job.job_reports;
    if (!rawReports) continue;

    const reports = Array.isArray(rawReports) ? rawReports : [];
    if (reports.length === 0) continue;

    const firmName =
      job.contract?.firm?.firm_name ||
      job.firm_short_name ||
      null;

    const technicalUser = job.job_assignment?.technical_user || null;
    const financialUser = job.job_assignment?.financial_user || null;
    const technicalUserId = technicalUser?.id ?? null;
    const technicalUserName = buildUserName(technicalUser);
    const financialUserId = financialUser?.id ?? null;
    const financialUserName = buildUserName(financialUser);

    for (const report of reports) {
      if (!report || !report.id) continue;

      const startDate = report.startDate || null;
      const endDate = report.endDate || null;
      const status = report.status || null;

      // Rapor türü ismi: önce reportTypeName (yeni kayıtlar), sonra ID→map lookup (eski kayıtlar)
      const reportTypeName =
        report.reportTypeName ||
        reportTypeMap[String(report.reportType)] ||
        null;

      const isActive = startDate ? startDate <= today : false;
      const isApproaching =
        startDate
          ? startDate > today && startDate <= sevenDaysFromNow
          : false;
      const isOverdue =
        endDate && status !== 'COMPLETED' ? endDate < today : false;
      const isCompleted = status === 'COMPLETED';
      const inFlow = inFlowKeySet.has(`${job.id}|${report.id}`);
      const financialRequestPending = financialRequestKeySet.has(
        `${job.id}|${report.id}`
      );

      allReports.push({
        reportId: report.id,
        reportType: report.reportType ?? null,       // ham değer (ID veya string)
        reportTypeName,                               // çözümlenmiş isim
        reportTags: report.reportTags ?? [],
        status,
        startDate,
        endDate,
        jobId: job.id,
        jobTitle: job.job_title || null,
        jobType: job.job_type || null,
        jobStatus: job.job_status || null,
        firmId: job.firm_id || null,
        firmName,
        projectNumber: job.project_number || null,
        companyId: job.company_id || null,
        technicalUserId,
        technicalUserName,
        financialUserId,
        financialUserName,
        inFlow,
        // String mirror so DataTable's `if (filter.value)` gate doesn't drop "false"
        inFlowLabel: inFlow ? 'true' : 'false',
        financialRequestPending,
        isActive,
        isApproaching,
        isOverdue,
        isCompleted,
      });
    }
  }

  return allReports;
};


const useActiveReports = () => {
  const { user } = useAuth();

  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isRoot = user?.role === 'ROOT';
  const companyWhere = isRoot ? {} : { company_id: { _eq: user?.companyId } };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Paralel: rapor türleri + işler
      const [typesResult, jobsResult] = await Promise.all([
        hasuraClient.query(GET_REPORT_TYPES_QUERY, {}),
        hasuraClient.query(
          GET_ACTIVE_REPORTS_BY_DATE,
          Object.keys(companyWhere).length > 0 ? { where: companyWhere } : {}
        ),
      ]);

      // ID → isim map'i oluştur
      const reportTypeMap = {};
      for (const rt of typesResult.report_types || []) {
        reportTypeMap[String(rt.id)] = rt.report_type_name;
      }

      // Akışa dahil edilmiş (job_id, report_id) çiftlerini set'e topla
      const inFlowKeySet = new Set();
      for (const item of jobsResult.report_flow_items || []) {
        if (item?.job_id != null && item?.report_id != null) {
          inFlowKeySet.add(`${item.job_id}|${item.report_id}`);
        }
      }

      // Bekleyen mali sorumlu talebi olan (job_id, report_id) çiftlerini set'e topla
      const financialRequestKeySet = new Set();
      for (const req of jobsResult.report_financial_requests || []) {
        if (req?.job_id != null && req?.report_id != null) {
          financialRequestKeySet.add(`${req.job_id}|${req.report_id}`);
        }
      }

      const jobs = jobsResult.jobs || [];
      const flattened = flattenJobReports(
        jobs,
        reportTypeMap,
        inFlowKeySet,
        financialRequestKeySet
      );
      setAllReports(flattened);
      return flattened;
    } catch (err) {
      setError(err.message || 'Aktif raporlar yüklenirken hata oluştu.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.companyId]);

  useEffect(() => {
    if (user) {
      fetchReports();
    }
  }, [fetchReports, user]);

  const activeReports = useMemo(
    () => allReports.filter((r) => r.isActive),
    [allReports]
  );

  const approachingReports = useMemo(
    () => allReports.filter((r) => r.isApproaching),
    [allReports]
  );

  const overdueReports = useMemo(
    () => allReports.filter((r) => r.isOverdue),
    [allReports]
  );

  const completedReports = useMemo(
    () => allReports.filter((r) => r.isCompleted),
    [allReports]
  );

  return {
    allReports,
    activeReports,
    approachingReports,
    overdueReports,
    completedReports,
    loading,
    error,
    refetch: fetchReports,
  };
};

export { useActiveReports };
export default useActiveReports;
